import { BfnStatus, BfnStatusTimestamp } from '@/models/Befehlsnachricht'
import BefehlsnachrichtStatusTransitionError from '@/features/befehlsnachrichten/errors/BefehlsnachrichtStatusTransitionError'
import BefehlsnachrichtUnknownVisuellerStatusError from '@/features/befehlsnachrichten/errors/BefehlsnachrichtUnknownVisuellerStatusError'
import BefehlsnachrichtVisuellerStatusMatchError from '@/features/befehlsnachrichten/errors/BefehlsnachrichtVisuellerStatusMatchError'

const stateMachine: Record<BfnStatus, BfnStatus[]> = {
  [BfnStatus.ERSTELLT]: [
    BfnStatus.SMS_IM_VERSAND,
    BfnStatus.SMS_VERSAND_ABGEBROCHEN,
    BfnStatus.ERSTABRUF_GESTARTET,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.SMS_IM_VERSAND]: [
    BfnStatus.SMS_ZUGESTELLT,
    BfnStatus.SMS_VERSAND_ABGEBROCHEN,
    BfnStatus.ERSTABRUF_GESTARTET,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.SMS_VERSAND_ABGEBROCHEN]: [
    BfnStatus.ERSTABRUF_GESTARTET,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.SMS_ZUGESTELLT]: [
    BfnStatus.ERSTABRUF_GESTARTET,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.ERSTABRUF_GESTARTET]: [
    BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH]: [
    BfnStatus.STANDORTEINGABE_AKZEPTIERT,
    BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH]: [
    BfnStatus.STANDORTEINGABE_AKZEPTIERT,
    BfnStatus.STANDORTABGLEICH_FEHLGESCHLAGEN,
    BfnStatus.GELOESCHT
  ],
  [BfnStatus.STANDORTEINGABE_AKZEPTIERT]: [
    BfnStatus.ABGERUFEN,
    BfnStatus.WIDERRUFEN
  ],
  [BfnStatus.ABGERUFEN]: [
    BfnStatus.QUITTIERT,
    BfnStatus.ABGEWIESEN,
    BfnStatus.WIDERRUFEN
  ],
  [BfnStatus.QUITTIERT]: [BfnStatus.ERLEDIGT, BfnStatus.WIDERRUFEN],
  [BfnStatus.ERLEDIGT]: [BfnStatus.WIDERRUFEN],
  [BfnStatus.GELOESCHT]: [],
  [BfnStatus.WIDERRUFEN]: [],
  [BfnStatus.STANDORTABGLEICH_FEHLGESCHLAGEN]: [],
  [BfnStatus.ABGEWIESEN]: []
}

const visuellerStatusMap: Map<string, BfnStatus[]> = new Map([
  ['Standorteingabe erforderlich', [BfnStatus.ERSTABRUF_GESTARTET]],
  [
    'Standortabgleich durch Fdl erforderlich',
    [BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH]
  ],
  [
    'Standortbestätigung erforderlich',
    [BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH]
  ],
  [
    'Standortabgleich fehlgeschlagen',
    [BfnStatus.STANDORTABGLEICH_FEHLGESCHLAGEN]
  ],
  ['Offen', [BfnStatus.STANDORTEINGABE_AKZEPTIERT, BfnStatus.ABGERUFEN]],
  ['Gültig', [BfnStatus.QUITTIERT]],
  ['Abgewiesen', [BfnStatus.ABGEWIESEN]],
  ['Gelöscht', [BfnStatus.GELOESCHT]],
  ['Widerrufen', [BfnStatus.WIDERRUFEN]],
  ['Erledigt', [BfnStatus.ERLEDIGT]]
])

export interface StatusStyle {
  backgroundColor: string
  textColor: string
  darkBackgroundColor: string
}

const statusStylesMap: Record<BfnStatus, StatusStyle> = {
  [BfnStatus.ERSTELLT]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.SMS_IM_VERSAND]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.SMS_VERSAND_ABGEBROCHEN]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.SMS_ZUGESTELLT]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.ERSTABRUF_GESTARTET]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH]: {
    backgroundColor: 'bg-blue-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-blue-600'
  },
  [BfnStatus.STANDORTABGLEICH_FEHLGESCHLAGEN]: {
    backgroundColor: 'bg-red-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-red-600'
  },
  [BfnStatus.STANDORTEINGABE_AKZEPTIERT]: {
    backgroundColor: 'bg-yellow-500',
    textColor: 'text-black',
    darkBackgroundColor: 'dark:bg-yellow-600'
  },
  [BfnStatus.ABGERUFEN]: {
    backgroundColor: 'bg-yellow-500',
    textColor: 'text-black',
    darkBackgroundColor: 'dark:bg-yellow-600'
  },
  [BfnStatus.QUITTIERT]: {
    backgroundColor: 'bg-light-green-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-light-green-600'
  },
  [BfnStatus.ERLEDIGT]: {
    backgroundColor: 'bg-cool-gray-200',
    textColor: 'text-black',
    darkBackgroundColor: 'dark:bg-cool-gray-200'
  },
  [BfnStatus.ABGEWIESEN]: {
    backgroundColor: 'bg-red-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-red-600'
  },
  [BfnStatus.GELOESCHT]: {
    backgroundColor: 'bg-red-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-red-600'
  },
  [BfnStatus.WIDERRUFEN]: {
    backgroundColor: 'bg-red-500',
    textColor: 'text-white',
    darkBackgroundColor: 'dark:bg-red-600'
  }
}

export function tfStatusStyle(
  status: BfnStatus,
  manuellWiderrufen?: boolean
): StatusStyle {
  // SwRS_19421_S0_R: high priority
  if (status === BfnStatus.WIDERRUFEN) {
    return statusStylesMap[BfnStatus.WIDERRUFEN]
  }
  // medium priority
  if (manuellWiderrufen) {
    return {
      backgroundColor: 'bg-red-500',
      textColor: 'text-white',
      darkBackgroundColor: 'dark:bg-red-600'
    }
  }
  // low priority
  return statusStylesMap[status]
}

export function tfStatusText(
  status: BfnStatus,
  visuellerStatus: string,
  manuellWiderrufen?: boolean
): string {
  // SwRS_19421_S0_R: high priority
  if (status === BfnStatus.WIDERRUFEN) {
    return 'Widerrufen'
  }
  // medium priority
  if (manuellWiderrufen) {
    return 'Widerrufen (manuell)'
  }
  // low priority
  return visuellerStatus
}

/**
 * Überprüft, ob der visuelle Status dem technischen Status entsprechend ist.
 *
 * @param visuellerStatus der visuelle Status
 * @param status der technische Status
 */
export function checkVisuellerStatusMatchesStatus(
  visuellerStatus: string,
  status: BfnStatus
): {
  error:
    | null
    | BefehlsnachrichtUnknownVisuellerStatusError
    | BefehlsnachrichtVisuellerStatusMatchError
} {
  const allowedStatus = visuellerStatusMap.get(visuellerStatus)
  if (!allowedStatus) {
    return {
      error: new BefehlsnachrichtUnknownVisuellerStatusError(visuellerStatus)
    }
  }
  if (!allowedStatus.includes(status)) {
    return {
      error: new BefehlsnachrichtVisuellerStatusMatchError(
        visuellerStatus,
        status
      )
    }
  }
  return { error: null }
}

/**
 * Überprüft die Gültigkeit eines Statuswechsels und die Konsistenz der Statushistorie.
 *
 * Diese Funktion führt drei Hauptprüfungen durch:
 *
 * 1. **Gültigkeit der Transition zwischen `expectedStatus` und `currentStatus`:**
 *    - Zuerst wird überprüft, ob der `currentStatus` entweder identisch mit dem `expectedStatus` ist
 *      oder ein gültiger Folgestatus davon gemäß der `stateMachine` ist.
 *    - Wenn `currentStatus` und `expectedStatus` identisch sind, wird dies als gültig betrachtet.
 *    - Andernfalls wird geprüft, ob der `currentStatus` als direkter Folgestatus von `expectedStatus`
 *      in der `stateMachine` definiert ist.
 *
 * 2. **Überprüfung der logischen Reihenfolge der Status in `statusTimestamps`:**
 *    - Nachdem sichergestellt wurde, dass die Transition von `expectedStatus` zu `currentStatus` gültig ist,
 *      wird die Reihenfolge der Statusübergänge innerhalb der `statusTimestamps`-Liste überprüft.
 *    - Es wird sichergestellt, dass jeder Übergang von einem Status zum nächsten in der Liste
 *      den Regeln der `stateMachine` entspricht.
 *
 * 3. **Überprüfung der Vollständigkeit der Status bis zum `currentStatus`:**
 *    - Schließlich wird überprüft, ob alle erforderlichen Status vom `ERSTABRUF_GESTARTET`-Status bis zum `currentStatus`
 *      in der `statusTimestamps`-Liste vorhanden sind.
 *    - Die Funktion geht dabei vom `ERSTABRUF_GESTARTET`-Status aus und verfolgt die Übergänge gemäß der `stateMachine`,
 *      bis der `currentStatus` erreicht wird. Dabei wird sichergestellt, dass kein Status auf dem Weg fehlt.
 *
 * Wenn eine der Prüfungen fehlschlägt, wird ein `BefehlsnachrichtStatusTransitionError` ausgelöst,
 * um anzuzeigen, dass der Statuswechsel oder die Statushistorie ungültig ist.
 *
 * @param currentStatus - Der aktuelle Status des Befehls.
 * @param expectedStatus - Der erwartete Status, zu dem der Befehl wechseln sollte.
 * @param statusTimestamps - Eine Liste von Zeitstempeln, die die Statushistorie des Befehls darstellen.
 * @throws {BefehlsnachrichtStatusTransitionError} Wenn ein Statuswechsel ungültig ist oder ein erwarteter Status fehlt.
 */
export function validateBfnStatusChange(
  currentStatus: BfnStatus,
  expectedStatus: BfnStatus,
  statusTimestamps: BfnStatusTimestamp[]
) {
  // 1. Überprüfen der Transition zwischen `expectedStatus` und `currentStatus`
  if (currentStatus !== expectedStatus) {
    const possibleNextStatuses: BfnStatus[] = stateMachine[expectedStatus]
    if (!possibleNextStatuses.includes(currentStatus)) {
      throw new BefehlsnachrichtStatusTransitionError(
        `Nach der zuletzt ausgeführten Aktion an der Befehlsnachricht wurde ein unerwarteter Statuswechsel festgestellt: (${BfnStatus[currentStatus]}) ist kein gültiger Folgestatus von ${BfnStatus[expectedStatus]}.`
      )
    }
  }

  // 2. Überprüfen der logischen Reihenfolge der Status in `statusTimestamps`
  for (const timestamp of statusTimestamps.slice(0, -1)) {
    const index = statusTimestamps.slice(0, -1).indexOf(timestamp)
    const fromStatus = timestamp.status
    const toStatus = statusTimestamps[index + 1].status

    if (!stateMachine[fromStatus]?.includes(toStatus)) {
      throw new BefehlsnachrichtStatusTransitionError(
        `Nach der zuletzt ausgeführten Aktion an der Befehlsnachricht wurde ein unerwarteter Statuswechsel von ${BfnStatus[fromStatus]} zu ${BfnStatus[toStatus]} festgestellt.`
      )
    }
  }

  // 3. Überprüfen, ob alle erforderlichen Status bis `currentStatus` vorhanden sind
  let current = BfnStatus.ERSTABRUF_GESTARTET
  const requiredStatuses = new Set<BfnStatus>()

  while (current !== currentStatus) {
    requiredStatuses.add(current)
    const nextStatuses: BfnStatus[] = stateMachine[current]
    const nextStatus = nextStatuses.find((status) =>
      statusTimestamps.some((st) => st.status === status)
    )
    if (!nextStatus) {
      throw new BefehlsnachrichtStatusTransitionError(
        `Nach der zuletzt ausgeführten Aktion an der Befehlsnachricht wurde ein unerwarteter Statuswechsel festgestellt: Zeitstempel für den nächsten erwarteten Status von ${BfnStatus[current]} fehlt.`
      )
    }
    current = nextStatus
  }
  requiredStatuses.add(currentStatus)

  // Überprüfen, ob alle erforderlichen Status in den Timestamps vorhanden sind
  for (const requiredStatus of Array.from(requiredStatuses)) {
    if (!statusTimestamps.some((st) => st.status === requiredStatus)) {
      throw new BefehlsnachrichtStatusTransitionError(
        `Nach der zuletzt ausgeführten Aktion an der Befehlsnachricht wurde ein unerwarteter Statuswechsel festgestellt: Zeitstempel für den Status ${BfnStatus[requiredStatus]} fehlt.`
      )
    }
  }
}
